import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getReceivingOrders,
  getReceivingOrder,
  getReceivingSummary,
  createReceivingOrder,
  updateReceivingOrder,
  deleteReceivingOrder,
  addReceivingLine,
  updateReceivingLine,
  removeReceivingLine,
  startReceiving,
  receiveLine,
  updateLineQcStatus,
  completeReceiving,
  cancelReceiving,
} from '../api';
import type {
  ReceivingFilters,
  CreateReceivingOrderInput,
  UpdateReceivingOrderInput,
  AddReceivingLineInput,
  UpdateReceivingLineInput,
  ReceiveLineInput,
  UpdateQCStatusInput,
} from '../types';
import { wmsInventoryKeys } from './useWmsInventory';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// Query keys
export const wmsReceivingKeys = {
  all: ['wms-receiving'] as const,
  // No-arg call must return a prefix so invalidateQueries({ queryKey: list() })
  // partial-matches every filtered list key. Including an `undefined` slot
  // makes it match nothing (typeof undefined !== typeof {}), which left the
  // list stale after create/update until a manual refresh.
  list: (filters?: ReceivingFilters) => filters !== undefined
    ? [...wmsReceivingKeys.all, 'list', filters] as const
    : [...wmsReceivingKeys.all, 'list'] as const,
  detail: (id: string) => [...wmsReceivingKeys.all, 'detail', id] as const,
  summary: () => [...wmsReceivingKeys.all, 'summary'] as const,
};

// ============================================================================
// Receiving Order Query Hooks
// ============================================================================

export function useReceivingOrders(filters: ReceivingFilters = {}) {
  return useQuery({
    queryKey: wmsReceivingKeys.list(filters),
    queryFn: () => getReceivingOrders(filters),
  });
}

export function useReceivingOrder(id: string) {
  return useQuery({
    queryKey: wmsReceivingKeys.detail(id),
    queryFn: () => getReceivingOrder(id),
    enabled: !!id,
  });
}

export function useReceivingSummary() {
  return useQuery({
    queryKey: wmsReceivingKeys.summary(),
    queryFn: getReceivingSummary,
  });
}

// ============================================================================
// Create/Update/Delete Receiving Order Hooks
// ============================================================================

export function useCreateReceivingOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReceivingOrderInput) => createReceivingOrder(data),
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.summary() });
      toast.success(`Receiving order "${order.order_number}" created`);
    },
    onError: (error) => {
      toast.error('Failed to create procurement order', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateReceivingOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReceivingOrderInput }) =>
      updateReceivingOrder(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      toast.success('Receiving order updated');
    },
    onError: (error) => {
      toast.error('Failed to update receiving order', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteReceivingOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteReceivingOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.summary() });
      toast.success('Receiving order deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete receiving order', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Receiving Line Hooks
// ============================================================================

export function useAddReceivingLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: AddReceivingLineInput }) =>
      addReceivingLine(orderId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      toast.success('Line added to receiving order');
    },
    onError: (error) => {
      toast.error('Failed to add line', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateReceivingLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, lineId, data }: { orderId: string; lineId: string; data: UpdateReceivingLineInput }) =>
      updateReceivingLine(orderId, lineId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.detail(result.id) });
      toast.success('Line updated');
    },
    onError: (error) => {
      toast.error('Failed to update line', { description: getErrorMessage(error) });
    },
  });
}

export function useRemoveReceivingLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, lineId }: { orderId: string; lineId: string }) =>
      removeReceivingLine(orderId, lineId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      toast.success('Line removed');
    },
    onError: (error) => {
      toast.error('Failed to remove line', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Receiving Workflow Hooks
// ============================================================================

export function useStartReceiving() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => startReceiving(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.summary() });
      toast.success('Receiving started');
    },
    onError: (error) => {
      toast.error('Failed to start receiving', { description: getErrorMessage(error) });
    },
  });
}

export function useReceiveLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, lineId, data }: { orderId: string; lineId: string; data: ReceiveLineInput }) =>
      receiveLine(orderId, lineId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.summary() });
      toast.success('Items received successfully');
    },
    onError: (error) => {
      toast.error('Failed to receive items', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateLineQcStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, lineId, data }: { orderId: string; lineId: string; data: UpdateQCStatusInput }) =>
      updateLineQcStatus(orderId, lineId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      toast.success('QC status updated');
    },
    onError: (error) => {
      toast.error('Failed to update QC status', { description: getErrorMessage(error) });
    },
  });
}

export function useCompleteReceiving() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => completeReceiving(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.summary() });
      // Invalidate inventory as items have been received
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      // Invalidate serialized items if any were created
      if (result.serials_created > 0) {
        queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
        queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serializedSummary() });
      }
      toast.success('Receiving completed - items added to inventory');
    },
    onError: (error) => {
      toast.error('Failed to complete receiving', { description: getErrorMessage(error) });
    },
  });
}

export function useCancelReceiving() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      cancelReceiving(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsReceivingKeys.summary() });
      toast.success('Receiving order cancelled');
    },
    onError: (error) => {
      toast.error('Failed to cancel receiving', { description: getErrorMessage(error) });
    },
  });
}
